import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Checkbox } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import theme from "../../theme";
import { groupByDomain, prettyDomain, cleanPermissionLabel } from "./utils";

const useStyles = makeStyles(() => ({
  wrap: {
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "10px",
    overflow: "hidden",
  },
  searchRow: {
    padding: "10px 12px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  search: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    background: "transparent",
  },
  selectedCount: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    whiteSpace: "nowrap",
  },
  list: {
    maxHeight: "320px",
    overflowY: "auto",
  },
  domainBlock: { borderBottom: `1px solid ${theme.colors.Zumthor}` },
  domainHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "#F9FBFF",
    cursor: "pointer",
    userSelect: "none",
  },
  domainTitle: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  domainSelectAll: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.DodgerBlue,
    cursor: "pointer",
  },
  permRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    padding: "4px 12px 4px 8px",
  },
  permLabel: { flex: 1, paddingTop: "9px" },
  permName: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
  permMeta: {
    fontSize: "11.5px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.6,
  },
  tag: {
    display: "inline-block",
    marginLeft: "8px",
    padding: "1px 7px",
    borderRadius: "999px",
    fontSize: "10.5px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    backgroundColor: theme.colors.Gallery,
    color: theme.colors.Violet,
    verticalAlign: "middle",
  },
  empty: {
    padding: "24px",
    textAlign: "center",
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "14px",
  },
  loading: { display: "flex", justifyContent: "center", padding: "30px" },
}));

// Checklist of capability codes, grouped by domain. `value` is an array of codes;
// `onChange(nextCodes)` returns the full selected set (never free-text).
function PermissionChecklist({ catalogue = [], loading = false, value = [], onChange }) {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const selected = useMemo(() => new Set(value), [value]);

  const grouped = useMemo(() => {
    const groups = groupByDomain(catalogue);
    if (!query.trim()) return groups;
    const q = query.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter(
          (p) =>
            p.name?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.domain?.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.permissions.length);
  }, [catalogue, query]);

  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(Array.from(next));
  };

  const toggleDomain = (permissions, allSelected) => {
    const next = new Set(selected);
    permissions.forEach((p) => {
      if (allSelected) next.delete(p.name);
      else next.add(p.name);
    });
    onChange(Array.from(next));
  };

  if (loading) {
    return (
      <div className={classes.wrap}>
        <div className={classes.loading}>
          <ThreeDots color={theme.colors.DodgerBlue} height={40} width={40} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      <div className={classes.searchRow}>
        <input
          className={classes.search}
          placeholder="Search permissions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search permissions"
        />
        <span className={classes.selectedCount}>{selected.size} selected</span>
      </div>

      <div className={classes.list}>
        {grouped.length === 0 ? (
          <div className={classes.empty}>No permissions match “{query}”.</div>
        ) : (
          grouped.map(({ domain, permissions }) => {
            const allSelected = permissions.every((p) => selected.has(p.name));
            const isCollapsed = !!collapsed[domain];
            return (
              <div key={domain} className={classes.domainBlock}>
                <div
                  className={classes.domainHeader}
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [domain]: !c[domain] }))
                  }
                >
                  <span className={classes.domainTitle}>
                    {isCollapsed ? "▸" : "▾"} {prettyDomain(domain)} (
                    {permissions.length})
                  </span>
                  <span
                    className={classes.domainSelectAll}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDomain(permissions, allSelected);
                    }}
                  >
                    {allSelected ? "Clear all" : "Select all"}
                  </span>
                </div>

                {!isCollapsed &&
                  permissions.map((perm) => (
                    <label key={perm.name} className={classes.permRow}>
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={selected.has(perm.name)}
                        onChange={() => toggle(perm.name)}
                        inputProps={{ "aria-label": perm.name }}
                      />
                      <span className={classes.permLabel}>
                        <span className={classes.permName}>
                          {cleanPermissionLabel(perm)}
                          {perm.enforced === false ? (
                            <span
                              className={classes.tag}
                              title="Controls dashboard nav visibility only — not a backend access gate."
                            >
                              dashboard-only
                            </span>
                          ) : null}
                        </span>
                        <div className={classes.permMeta}>
                          {perm.name} · {perm.action}
                        </div>
                      </span>
                    </label>
                  ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default PermissionChecklist;
